import React from 'react';
import { LucideIcon, LucideProps } from 'lucide-react';

// Re-export commonly used icons for easy access
export {
  // Navigation
  Home,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  ExternalLink,

  // Actions
  Search,
  Plus,
  Minus,
  Check,
  Copy,
  Share2 as Share,
  Download,
  Upload,
  RefreshCw as Refresh,
  RotateCcw as Reset,

  // Communication
  Mail,
  Phone,
  MessageCircle,
  Send,

  // Social
  Facebook,
  Instagram,
  Twitter,
  Linkedin,

  // E-commerce
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  Truck,
  Package,
  Tag,

  // Content
  Heart,
  Star,
  Bookmark,
  Clock,
  Calendar,
  MapPin,
  Globe,

  // Media
  Image,
  Camera,
  Play,
  Pause,
  Volume2 as Volume,
  VolumeX,

  // Files
  File,
  FileText,
  Folder,
  Trash2 as Trash,
  Edit2 as Edit,

  // UI
  Eye,
  EyeOff,
  Info,
  AlertCircle,
  AlertTriangle,
  HelpCircle,
  Settings,
  User,
  Users,
  LogIn,
  LogOut,
  Lock,
  Unlock,

  // Misc
  Leaf,
  Sparkles,
  Award,
  Shield,
  Zap,
  Sun,
  Moon,
  Filter,
  SlidersHorizontal as Sliders,
  LayoutGrid,
  List,
  MoreHorizontal,
  MoreVertical,
  Loader2 as Loader,
} from 'lucide-react';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const sizeMap: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  '2xl': 48,
};

export interface IconProps extends Omit<LucideProps, 'size'> {
  /** The Lucide icon component to render */
  icon: LucideIcon;
  /** Predefined size or custom number */
  size?: IconSize | number;
  /** Additional CSS classes */
  className?: string;
  /** Accessible label (sets aria-label) */
  label?: string;
}

/**
 * Icon wrapper component for consistent icon styling across the app.
 * Uses lucide-react icons with predefined sizes and accessibility support.
 *
 * @example
 * ```tsx
 * import { Icon, Search, ShoppingCart } from '../components/UI/Icon';
 *
 * <Icon icon={Search} size="md" />
 * <Icon icon={ShoppingCart} size="lg" className="text-natuvia-cta" />
 * ```
 */
export const Icon: React.FC<IconProps> = ({
  icon: LucideIconComponent,
  size = 'md',
  className = '',
  label,
  ...props
}) => {
  const pixelSize = typeof size === 'number' ? size : sizeMap[size];

  return (
    <LucideIconComponent
      size={pixelSize}
      className={className}
      aria-label={label}
      aria-hidden={!label}
      {...props}
    />
  );
};

/**
 * Spinning loader icon for loading states
 */
export const LoadingSpinner: React.FC<{
  size?: IconSize | number;
  className?: string;
}> = ({ size = 'md', className = '' }) => {
  const pixelSize = typeof size === 'number' ? size : sizeMap[size];

  return (
    <svg
      className={`animate-spin ${className}`}
      width={pixelSize}
      height={pixelSize}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

export default Icon;
